<template>
  <div class="row">
    <div class="col-md-offset-1 col-md-10">
      <div class="card" v-loading="loading">
        <div class="card-content">
          <h4 class="purple text-center">{{ $t('marketingAutomation.details') }}</h4>

          <!-- Common to every type: name, period, active -->
          <div class="row">
            <div class="col-md-5 col-md-offset-1">
              <div class="form-group">
                <label>{{ $t('marketingAutomation.name') }}*</label>
                <input type="text"
                       class="form-control"
                       maxlength="255"
                       :placeholder="$t('marketingAutomation.name')"
                       v-model="form.name">
                <small class="text-danger" v-if="formErrors.name">{{ formErrors.name }}</small>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label>{{ $t('marketingAutomation.period') }}*</label>
                <br/>
                <el-date-picker type="daterange"
                                v-model="form.period"
                                format="dd.MM.yyyy"
                                value-format="yyyy-MM-dd">
                </el-date-picker>
                <br/>
                <small class="text-danger" v-if="formErrors.period">{{ formErrors.period }}</small>
              </div>
            </div>
            <div class="col-md-1">
              <div class="form-group">
                <label>{{ $t('marketingAutomation.active') }}</label>
                <br/>
                <p-switch v-model="form.active" type="success"></p-switch>
              </div>
            </div>
          </div>

          <!-- Type: fixed after the campaign is created (each type has its own settings) -->
          <div class="row">
            <div class="col-md-10 col-md-offset-1">
              <div class="form-group">
                <label>{{ $t('marketingAutomation.type') }}*</label>
                <div class="automation-types">
                  <div v-for="item in types"
                       :key="item.value"
                       class="automation-type-card"
                       :class="{ 'is-selected': form.type === item.value, 'is-disabled': !canPickType(item) }"
                       @click="pickType(item)">
                    <i :class="item.icon"></i>
                    <span>{{ $t(item.label) }}</span>
                    <small v-if="!item.available" class="automation-type-card__soon">{{ $t('marketingAutomation.comingSoon') }}</small>
                  </div>
                </div>
                <small class="text-muted" v-if="isEdit">{{ $t('marketingAutomation.typeLocked') }}</small>
                <small class="text-danger" v-if="formErrors.type">{{ formErrors.type }}</small>
              </div>
            </div>
          </div>
          <hr>

          <!-- Settings of the type (birthday / forgotten cart) + preview -->
          <div class="row" v-if="form.type === TYPE_BIRTHDAY || form.type === TYPE_CART">
            <div class="col-md-10 col-md-offset-1">
              <div class="automation-info">
                <i class="fa fa-info-circle"></i> {{ form.type === TYPE_CART ? $t('marketingAutomation.cartRule') : $t('marketingAutomation.birthdayRule') }}
              </div>

              <div class="row">
                <div class="col-md-7">
                  <template v-if="form.type === TYPE_BIRTHDAY">
                  <!-- 1. Announcement X days before -->
                  <div class="automation-box" :class="{ 'is-off': !form.settings.announce_enabled }">
                    <div class="automation-box__head">
                      <p-switch v-model="form.settings.announce_enabled" type="success"></p-switch>
                      <strong>{{ $t('marketingAutomation.announceTitleBox') }}</strong>
                      <a v-if="form.settings.announce_enabled" class="automation-example-link" @click.prevent="useExample('announce')">
                        <i class="fa fa-magic"></i> {{ $t('marketingAutomation.useExample') }}
                      </a>
                    </div>
                    <template v-if="form.settings.announce_enabled">
                      <div class="form-group automation-days">
                        <label>{{ $t('marketingAutomation.announceDaysBefore') }}*</label>
                        <div class="automation-days__row">
                          <input type="number" min="1" :max="ANNOUNCE_DAYS_MAX" step="1" class="form-control" v-model.number="form.settings.announce_days_before">
                          <span>{{ $t('marketingAutomation.daysBeforeBirthday') }}</span>
                        </div>
                        <small class="text-danger" v-if="formErrors.announce_days_before">{{ formErrors.announce_days_before }}</small>
                      </div>
                      <div class="form-group">
                        <label>{{ $t('marketingAutomation.pushTitle') }}*</label>
                        <input type="text" class="form-control" :maxlength="TITLE_MAX" v-model="form.settings.announce_title" :placeholder="example('exampleAnnounceTitle')" @focus="focused = 'announce'">
                        <small class="automation-count">{{ (form.settings.announce_title || '').length }}/{{ TITLE_MAX }}</small>
                        <small class="text-danger" v-if="formErrors.announce_title">{{ formErrors.announce_title }}</small>
                      </div>
                      <div class="form-group">
                        <label>{{ $t('marketingAutomation.pushMessage') }}*</label>
                        <textarea rows="4" class="form-control" :maxlength="MESSAGE_MAX" v-model="form.settings.announce_message" :placeholder="example('exampleAnnounceMessage')" @focus="focused = 'announce'"></textarea>
                        <small class="automation-count">{{ (form.settings.announce_message || '').length }}/{{ MESSAGE_MAX }}</small>
                        <small class="text-danger" v-if="formErrors.announce_message">{{ formErrors.announce_message }}</small>
                      </div>
                    </template>
                    <small v-else class="text-muted">{{ $t('marketingAutomation.announceOffHint') }}</small>
                  </div>

                  <!-- 2. On the day -->
                  <div class="automation-box">
                    <div class="automation-box__head">
                      <i class="fa fa-birthday-cake"></i>
                      <strong>{{ $t('marketingAutomation.dayTitleBox') }}</strong>
                      <a class="automation-example-link" @click.prevent="useExample('day')">
                        <i class="fa fa-magic"></i> {{ $t('marketingAutomation.useExample') }}
                      </a>
                    </div>
                    <div class="form-group">
                      <label>{{ $t('marketingAutomation.pushTitle') }}*</label>
                      <input type="text" class="form-control" :maxlength="TITLE_MAX" v-model="form.settings.day_title" :placeholder="example('exampleDayTitle')" @focus="focused = 'day'">
                      <small class="automation-count">{{ (form.settings.day_title || '').length }}/{{ TITLE_MAX }}</small>
                      <small class="text-danger" v-if="formErrors.day_title">{{ formErrors.day_title }}</small>
                    </div>
                    <div class="form-group">
                      <label>{{ $t('marketingAutomation.pushMessage') }}*</label>
                      <textarea rows="4" class="form-control" :maxlength="MESSAGE_MAX" v-model="form.settings.day_message" :placeholder="example('exampleDayMessage')" @focus="focused = 'day'"></textarea>
                      <small class="automation-count">{{ (form.settings.day_message || '').length }}/{{ MESSAGE_MAX }}</small>
                      <small class="text-danger" v-if="formErrors.day_message">{{ formErrors.day_message }}</small>
                    </div>
                  </div>

                  <div class="form-group">
                    <label>{{ $t('marketingAutomation.sendTime') }}</label>
                    <br/>
                    <el-time-select v-model="form.settings.send_time"
                                    :clearable="false"
                                    :picker-options="{ start: '06:00', step: '00:30', end: '22:00' }">
                    </el-time-select>
                    <br/>
                    <small class="text-muted">{{ $t('marketingAutomation.sendTimeHint') }}</small>
                    <small class="text-danger" v-if="formErrors.send_time">{{ formErrors.send_time }}</small>
                  </div>
                  </template>

                  <!-- Forgotten cart settings -->
                  <template v-if="form.type === TYPE_CART">
                  <!-- 1. Push 1 (carries the coupon) -->
                  <div class="automation-box">
                    <div class="automation-box__head">
                      <i class="fa fa-shopping-cart"></i>
                      <strong>{{ $t('marketingAutomation.cartFirstBox') }}</strong>
                      <span class="automation-variant" :class="{ 'is-coupon': form.coupon_ids.length }">{{ form.coupon_ids.length ? $t('marketingAutomation.variantWithCoupon') : $t('marketingAutomation.variantNoCampaignCoupon') }}</span>
                      <a class="automation-example-link" @click.prevent="useExample('first')">
                        <i class="fa fa-magic"></i> {{ $t('marketingAutomation.useExample') }}
                      </a>
                    </div>
                    <div class="form-group automation-days">
                      <label>{{ $t('marketingAutomation.cartFirstDelay') }}*</label>
                      <div class="automation-days__row">
                        <input type="number" :min="DELAY_MIN" :max="FIRST_DELAY_MAX" step="1" class="form-control" v-model.number="form.settings.first_delay_minutes">
                        <span>{{ $t('marketingAutomation.minutesAfterChange') }} <span class="automation-duration">{{ durationText(form.settings.first_delay_minutes) }}</span></span>
                      </div>
                      <small class="text-danger" v-if="formErrors.first_delay_minutes">{{ formErrors.first_delay_minutes }}</small>
                    </div>
                    <div class="form-group">
                      <label>{{ $t('marketingAutomation.pushTitle') }}*</label>
                      <input type="text" class="form-control" :maxlength="TITLE_MAX" v-model="form.settings.first_title" :placeholder="example('exampleCartFirstTitle')" @focus="focused = 'first'">
                      <small class="automation-count">{{ (form.settings.first_title || '').length }}/{{ TITLE_MAX }}</small>
                      <small class="text-danger" v-if="formErrors.first_title">{{ formErrors.first_title }}</small>
                    </div>
                    <div class="form-group">
                      <label>{{ $t('marketingAutomation.pushMessage') }}*</label>
                      <textarea rows="4" class="form-control" :maxlength="MESSAGE_MAX" v-model="form.settings.first_message" :placeholder="example('exampleCartFirstMessage')" @focus="focused = 'first'"></textarea>
                      <small class="automation-count">{{ (form.settings.first_message || '').length }}/{{ MESSAGE_MAX }}</small>
                      <small class="text-danger" v-if="formErrors.first_message">{{ formErrors.first_message }}</small>
                    </div>
                  </div>

                  <!-- 1b. Push 1 for the customers who get no coupon (recurrence / coupon off): always shown, usable once a coupon is selected -->
                  <div class="automation-box" :class="{ 'is-off': !form.coupon_ids.length }">
                    <div class="automation-box__head">
                      <i class="fa fa-bell-o"></i>
                      <strong>{{ $t('marketingAutomation.cartFirstBox') }}</strong>
                      <span class="automation-variant">{{ $t('marketingAutomation.variantNoCoupon') }}</span>
                      <a v-if="form.coupon_ids.length" class="automation-example-link" @click.prevent="useExample('nocoupon')">
                        <i class="fa fa-magic"></i> {{ $t('marketingAutomation.useExample') }}
                      </a>
                    </div>
                    <small class="text-muted automation-box__hint">{{ $t('marketingAutomation.cartNoCouponHint') }}</small>
                    <small v-if="!form.coupon_ids.length" class="automation-placeholder__off automation-box__hint"><i class="fa fa-lock"></i> {{ $t('marketingAutomation.noCouponNeedsCoupon') }}</small>
                    <div class="form-group">
                      <label>{{ $t('marketingAutomation.pushTitle') }}*</label>
                      <input type="text" class="form-control" :maxlength="TITLE_MAX" v-model="form.settings.nocoupon_title" :disabled="!form.coupon_ids.length" :placeholder="example('exampleCartNoCouponTitle')" @focus="focused = 'nocoupon'">
                      <small class="automation-count">{{ (form.settings.nocoupon_title || '').length }}/{{ TITLE_MAX }}</small>
                      <small class="text-danger" v-if="formErrors.nocoupon_title">{{ formErrors.nocoupon_title }}</small>
                    </div>
                    <div class="form-group">
                      <label>{{ $t('marketingAutomation.pushMessage') }}*</label>
                      <textarea rows="4" class="form-control" :maxlength="MESSAGE_MAX" v-model="form.settings.nocoupon_message" :disabled="!form.coupon_ids.length" :placeholder="example('exampleCartNoCouponMessage')" @focus="focused = 'nocoupon'"></textarea>
                      <small class="automation-count">{{ (form.settings.nocoupon_message || '').length }}/{{ MESSAGE_MAX }}</small>
                      <small class="text-danger" v-if="formErrors.nocoupon_message">{{ formErrors.nocoupon_message }}</small>
                    </div>
                  </div>

                  <!-- 2. Push 2, after push 1 -->
                  <div class="automation-box" :class="{ 'is-off': !form.settings.second_enabled }">
                    <div class="automation-box__head">
                      <p-switch v-model="form.settings.second_enabled" type="success"></p-switch>
                      <strong>{{ $t('marketingAutomation.cartSecondBox') }}</strong>
                      <a v-if="form.settings.second_enabled" class="automation-example-link" @click.prevent="useExample('second')">
                        <i class="fa fa-magic"></i> {{ $t('marketingAutomation.useExample') }}
                      </a>
                    </div>
                    <template v-if="form.settings.second_enabled">
                      <small class="text-muted automation-box__hint">{{ $t('marketingAutomation.cartSecondHint', { cod: '{cod}' }) }}</small>
                      <div class="form-group automation-days">
                        <label>{{ $t('marketingAutomation.cartSecondDelay') }}*</label>
                        <div class="automation-days__row">
                          <input type="number" :min="DELAY_MIN" :max="SECOND_DELAY_MAX" step="1" class="form-control" v-model.number="form.settings.second_delay_minutes">
                          <span>{{ $t('marketingAutomation.minutesAfterFirst') }} <span class="automation-duration">{{ durationText(form.settings.second_delay_minutes) }}</span></span>
                        </div>
                        <small class="text-danger" v-if="formErrors.second_delay_minutes">{{ formErrors.second_delay_minutes }}</small>
                      </div>
                      <div class="form-group">
                        <label>{{ $t('marketingAutomation.pushTitle') }}*</label>
                        <input type="text" class="form-control" :maxlength="TITLE_MAX" v-model="form.settings.second_title" :placeholder="example('exampleCartSecondTitle')" @focus="focused = 'second'">
                        <small class="automation-count">{{ (form.settings.second_title || '').length }}/{{ TITLE_MAX }}</small>
                        <small class="text-danger" v-if="formErrors.second_title">{{ formErrors.second_title }}</small>
                      </div>
                      <div class="form-group">
                        <label>{{ $t('marketingAutomation.pushMessage') }}*</label>
                        <textarea rows="4" class="form-control" :maxlength="MESSAGE_MAX" v-model="form.settings.second_message" :placeholder="example('exampleCartSecondMessage')" @focus="focused = 'second'"></textarea>
                        <small class="automation-count">{{ (form.settings.second_message || '').length }}/{{ MESSAGE_MAX }}</small>
                        <small class="text-danger" v-if="formErrors.second_message">{{ formErrors.second_message }}</small>
                      </div>
                    </template>
                    <small v-else class="text-muted">{{ $t('marketingAutomation.cartSecondOffHint') }}</small>
                  </div>

                  <!-- Quiet hours -->
                  <div class="automation-box" :class="{ 'is-off': !form.settings.quiet_enabled }">
                    <div class="automation-box__head">
                      <p-switch v-model="form.settings.quiet_enabled" type="success"></p-switch>
                      <strong>{{ $t('marketingAutomation.quietBox') }}</strong>
                    </div>
                    <template v-if="form.settings.quiet_enabled">
                      <div class="automation-quiet">
                        <span>{{ $t('marketingAutomation.quietFrom') }}</span>
                        <el-time-select v-model="form.settings.quiet_from" :clearable="false" class="automation-quiet__time"
                                        :picker-options="{ start: '00:00', step: '00:30', end: '23:30' }"></el-time-select>
                        <span>{{ $t('marketingAutomation.quietTo') }}</span>
                        <el-time-select v-model="form.settings.quiet_to" :clearable="false" class="automation-quiet__time"
                                        :picker-options="{ start: '00:00', step: '00:30', end: '23:30' }"></el-time-select>
                      </div>
                      <small class="text-muted">{{ $t('marketingAutomation.quietHint') }}</small>
                      <small class="text-danger" v-if="formErrors.quiet_from || formErrors.quiet_to">{{ formErrors.quiet_from || formErrors.quiet_to }}</small>
                    </template>
                    <small v-else class="text-muted">{{ $t('marketingAutomation.quietOffHint') }}</small>
                  </div>

                  </template>
                </div>

                <!-- Preview of the push being edited + placeholders -->
                <div class="col-md-5">
                  <label>{{ $t('marketingAutomation.preview') }}</label>
                  <div class="automation-preview">
                    <div class="automation-preview__app">
                      <i class="fa fa-bell"></i> {{ companyName }} · {{ previewWhen }}
                    </div>
                    <div class="automation-preview__title" :class="{ 'is-example': !previewPush.title }">{{ previewText(previewPush.title || previewExample.title) }}</div>
                    <div class="automation-preview__body" :class="{ 'is-example': !previewPush.message }">{{ previewText(previewPush.message || previewExample.message) }}</div>
                  </div>
                  <small v-if="!previewPush.title || !previewPush.message" class="text-muted automation-preview__note">{{ $t('marketingAutomation.previewExampleNote') }}</small>
                  <small v-if="codeMissing" class="automation-preview__warn">
                    <i class="fa fa-exclamation-triangle"></i> {{ $t('marketingAutomation.codeMissing', { cod: '{cod}' }) }}
                  </small>
                  <small v-if="codeWithoutCoupon" class="automation-preview__warn">
                    <i class="fa fa-info-circle"></i> {{ $t('marketingAutomation.codeWithoutCoupon', { cod: '{cod}' }) }}
                  </small>
                  <div class="automation-placeholders">
                    <small class="text-muted">{{ $t('marketingAutomation.placeholdersHint') }}</small>
                    <div v-for="item in placeholders" :key="item.key" class="automation-placeholder" :class="{ 'is-disabled': item.disabled }">
                      <code @click="!item.disabled && insertPlaceholder(item.key)"
                            :title="item.disabled ? $t(item.disabledHint) : $t('marketingAutomation.clickToInsert')">{{ item.key }}</code>
                      <small>{{ $t(item.label) }}</small>
                      <small v-if="item.disabled" class="automation-placeholder__off"><i class="fa fa-lock"></i> {{ $t(item.disabledHint) }}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr v-if="form.type">

          <!-- Coupons: searchable multiple picker (only coupons ticked "used in automatic marketing") -->
          <div class="row">
            <div class="col-md-10 col-md-offset-1">
              <div class="form-group">
                <div class="automation-coupons-head">
                  <label style="margin:0;">
                    {{ $t('marketingAutomation.coupons') }} <small class="text-muted">({{ $t('marketingAutomation.optional') }})</small>
                    <span class="automation-selected-count" v-if="form.coupon_ids.length">{{ $t('marketingAutomation.selectedCount', { count: form.coupon_ids.length }) }}</span>
                  </label>
                  <button class="btn btn-sm btn-purple" @click.prevent="openCouponModal">
                    <i class="fa fa-plus"></i> {{ $t('marketingAutomation.newCoupon') }}
                  </button>
                </div>
                <small class="text-muted" style="display:block; margin-bottom:8px;">{{ $t('marketingAutomation.couponsHint') }}</small>
                <p v-if="!coupons.length" class="text-muted">{{ $t('marketingAutomation.noCoupons') }}</p>
                <template v-else>
                  <input type="text" class="form-control automation-coupon-search" v-model="couponSearch" :placeholder="$t('marketingAutomation.searchCoupons')">
                  <div class="automation-coupon-list">
                    <div v-for="coupon in filteredCoupons" :key="'coupon' + coupon.id"
                         class="automation-coupon-row"
                         :class="{ 'is-checked': isCouponChecked(coupon.id) }"
                         @click="toggleCoupon(coupon.id)">
                      <input type="checkbox" :checked="isCouponChecked(coupon.id)" style="margin:0; pointer-events:none;">
                      <span>
                        <strong>{{ coupon.name }}</strong>
                        <code v-if="coupon.coupon_code" class="automation-coupon-code">{{ coupon.coupon_code }}</code>
                        <small class="text-muted"> — {{ couponDiscountText(coupon) }} · {{ couponValidityText(coupon) }}</small>
                        <span v-if="!coupon.active" class="automation-coupon-flag">{{ $t('marketingAutomation.inactive') }}</span>
                        <span v-else-if="couponExpired(coupon)" class="automation-coupon-flag automation-coupon-flag--warn">{{ $t('marketingAutomation.expired') }}</span>
                      </span>
                    </div>
                    <p v-if="!filteredCoupons.length" class="text-muted" style="padding:10px; margin:0;">{{ $t('marketingAutomation.noCouponsFound') }}</p>
                  </div>
                </template>
                <small class="text-danger" v-if="formErrors.coupon_ids">{{ formErrors.coupon_ids }}</small>
              </div>

              <!-- Forgotten cart: coupon recurrence (always visible, usable once a coupon is selected) -->
              <div v-if="form.type === TYPE_CART" class="automation-box automation-cooldown" :class="{ 'is-off': !form.coupon_ids.length }">
                <div class="automation-box__head">
                  <i class="fa fa-history"></i>
                  <strong>{{ $t('marketingAutomation.cooldownBox') }}</strong>
                </div>
                <div class="automation-days__row">
                  <span>{{ $t('marketingAutomation.cooldownLabel') }}</span>
                  <input type="number" min="0" :max="COOLDOWN_MAX" step="1" class="form-control"
                         :disabled="!form.coupon_ids.length"
                         v-model.number="form.settings.coupon_cooldown_days">
                  <span>{{ $t('marketingAutomation.cooldownDays') }}</span>
                </div>
                <small class="text-muted automation-cooldown__hint">{{ $t('marketingAutomation.cooldownHint') }}</small>
                <small v-if="!form.coupon_ids.length" class="automation-placeholder__off"><i class="fa fa-lock"></i> {{ $t('marketingAutomation.cooldownNeedsCoupon') }}</small>
                <small class="text-danger" v-if="formErrors.coupon_cooldown_days">{{ formErrors.coupon_cooldown_days }}</small>
              </div>
            </div>
          </div>

          <div class="text-center mar-to-10">
            <button class="btn btn-purple-light btn-fill btn-wd" :disabled="saving" @click.prevent="save()">{{ $t('general.save') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- New automatic marketing coupon without leaving the campaign -->
    <vmodal name="automationCouponModal"
            class="automationCouponModal"
            width="95%"
            :maxWidth="1100"
            :minWidth="300"
            height="auto"
            :scrollable="true"
            :adaptive="true"
            :clickToClose="false"
            @opened="onCouponModalOpened">
      <div class="automation-coupon-modal__header">
        <h4>{{ $t('marketingAutomation.newCouponTitle') }}</h4>
        <a class="automation-coupon-modal__close" @click="closeCouponModal"><i class="fa fa-times"></i></a>
      </div>
      <div class="automation-coupon-modal__body">
        <coupon-form v-if="couponModalOpen" embedded force-automation @saved="onCouponCreated"></coupon-form>
      </div>
    </vmodal>
  </div>
</template>
<script>
  import Vue from 'vue'
  import {DatePicker, TimeSelect} from 'element-ui'
  import swal from 'sweetalert2'
  import 'sweetalert2/dist/sweetalert2.css'
  import PSwitch from 'src/components/UIComponents/Switch.vue'
  import vmodal from 'vue-js-modal'
  import CouponForm from '../DiscountCoupons/CouponForm.vue'
  import { AUTOMATION_TYPES, AUTOMATION_TYPE_BIRTHDAY, AUTOMATION_TYPE_CART } from './automationTypes'

  Vue.use(DatePicker)
  Vue.use(TimeSelect)
  Vue.use(vmodal, { componentName: 'vmodal' })

  // Must match the API (AutomationCampaignSettings)
  const TITLE_MAX = 100
  const MESSAGE_MAX = 500
  const ANNOUNCE_DAYS_MAX = 30
  const DEFAULT_SEND_TIME = '10:00'
  // forgotten cart (minutes / days) - must match AutomationCampaignSettings
  const DELAY_MIN = 10
  const FIRST_DELAY_MAX = 1440
  const SECOND_DELAY_MAX = 4320
  const COOLDOWN_MAX = 365

  // the pushes of each type: settings fields + example texts (i18n marketingAutomation.*)
  const PUSHES = {
    announce: { title: 'announce_title', message: 'announce_message', exampleTitle: 'exampleAnnounceTitle', exampleMessage: 'exampleAnnounceMessage' },
    day: { title: 'day_title', message: 'day_message', exampleTitle: 'exampleDayTitle', exampleMessage: 'exampleDayMessage' },
    first: { title: 'first_title', message: 'first_message', exampleTitle: 'exampleCartFirstTitle', exampleMessage: 'exampleCartFirstMessage' },
    nocoupon: { title: 'nocoupon_title', message: 'nocoupon_message', exampleTitle: 'exampleCartNoCouponTitle', exampleMessage: 'exampleCartNoCouponMessage' },
    second: { title: 'second_title', message: 'second_message', exampleTitle: 'exampleCartSecondTitle', exampleMessage: 'exampleCartSecondMessage' }
  }
  // example texts used when the campaign has no coupon
  const NO_COUPON_EXAMPLES = {
    exampleAnnounceTitle: 'exampleAnnounceNoCouponTitle',
    exampleAnnounceMessage: 'exampleAnnounceNoCouponMessage',
    exampleDayTitle: 'exampleDayNoCouponTitle',
    exampleDayMessage: 'exampleDayNoCouponMessage',
    exampleCartFirstTitle: 'exampleCartNoCouponTitle',
    exampleCartFirstMessage: 'exampleCartNoCouponMessage'
  }

  export default {
    components: {
      [DatePicker.name]: DatePicker,
      [TimeSelect.name]: TimeSelect,
      PSwitch,
      CouponForm
    },
    props: {
      route: {
        type: Object,
        default: () => ({})
      }
    },
    data () {
      return {
        TYPE_BIRTHDAY: AUTOMATION_TYPE_BIRTHDAY,
        TYPE_CART: AUTOMATION_TYPE_CART,
        DELAY_MIN,
        FIRST_DELAY_MAX,
        SECOND_DELAY_MAX,
        COOLDOWN_MAX,
        TITLE_MAX,
        MESSAGE_MAX,
        ANNOUNCE_DAYS_MAX,
        types: AUTOMATION_TYPES,
        loading: false,
        saving: false,
        currency: '',
        companyName: '',
        form: {
          name: '',
          period: null,
          active: true,
          type: AUTOMATION_TYPE_BIRTHDAY,
          settings: this.defaultSettings(AUTOMATION_TYPE_BIRTHDAY),
          coupon_ids: []
        },
        formErrors: {},
        // which push the preview shows (the last one focused)
        focused: 'day',
        coupons: [],
        couponSearch: '',
        couponModalOpen: false
      }
    },
    computed: {
      campaignId () {
        return this.route.params && this.route.params.id
      },
      isEdit () {
        return !!this.campaignId
      },
      // the push being edited / previewed (one of PUSHES, shown only if it exists for the type)
      activePush () {
        const s = this.form.settings || {}
        if (this.form.type === AUTOMATION_TYPE_CART) {
          if (this.focused === 'nocoupon' && this.form.coupon_ids.length) {
            return 'nocoupon'
          }
          if (this.focused === 'second' && s.second_enabled) {
            return 'second'
          }
          return 'first'
        }
        return this.focused === 'announce' && s.announce_enabled ? 'announce' : 'day'
      },
      previewPush () {
        const s = this.form.settings || {}
        const push = PUSHES[this.activePush]
        return { title: s[push.title], message: s[push.message] }
      },
      previewWhen () {
        const s = this.form.settings || {}
        switch (this.activePush) {
          case 'announce':
            return this.$t('marketingAutomation.announceDaysBeforeShort', { days: s.announce_days_before || '?' })
          case 'first':
          case 'nocoupon':
            return this.$t('marketingAutomation.cartFirstWhen', { time: this.durationText(s.first_delay_minutes, false) })
          case 'second':
            return this.$t('marketingAutomation.cartSecondWhen', { time: this.durationText(s.second_delay_minutes, false) })
          default:
            return this.$t('marketingAutomation.onTheDayShort') + ' ' + (s.send_time || '')
        }
      },
      placeholders () {
        const list = [{ key: '{nume}', label: 'marketingAutomation.placeholderName' }]
        if (this.activePush === 'announce') {
          list.push({ key: '{zile}', label: 'marketingAutomation.placeholderDays' })
        }
        // the push without coupon has no code to show
        // {cod} is always listed, but usable only when the campaign gives coupons (and not in the push without coupon)
        const noCoupon = !this.form.coupon_ids.length
        list.push({
          key: '{cod}',
          label: 'marketingAutomation.placeholderCode',
          disabled: noCoupon || this.activePush === 'nocoupon',
          disabledHint: noCoupon ? 'marketingAutomation.codeNeedsCoupon' : 'marketingAutomation.codeNotInNoCoupon'
        })
        return list
      },
      // no coupon on the campaign but a text still uses {cod}: it is simply sent empty
      codeWithoutCoupon () {
        if (this.form.coupon_ids.length) {
          return false
        }
        const s = this.form.settings || {}
        return Object.keys(PUSHES).some(key => {
          const push = PUSHES[key]
          return [s[push.title], s[push.message]].some(text => (text || '').indexOf('{cod}') !== -1)
        })
      },
      // example texts of the push being previewed (shown while its fields are empty)
      previewExample () {
        const push = PUSHES[this.activePush]
        return { title: this.example(push.exampleTitle), message: this.example(push.exampleMessage) }
      },
      // informative codes of the selected coupons (what {cod} becomes)
      selectedCodes () {
        return this.coupons
          .filter(coupon => this.form.coupon_ids.indexOf(coupon.id) !== -1 && coupon.coupon_code)
          .map(coupon => coupon.coupon_code)
      },
      // a push uses {cod} but no selected coupon has a code
      codeMissing () {
        const s = this.form.settings || {}
        let texts = [s.day_title, s.day_message]
        if (this.form.type === AUTOMATION_TYPE_CART) {
          texts = [s.first_title, s.first_message]
          if (s.second_enabled) {
            texts.push(s.second_title, s.second_message)
          }
        } else if (s.announce_enabled) {
          texts.push(s.announce_title, s.announce_message)
        }
        const usesCode = texts.some(text => (text || '').indexOf('{cod}') !== -1)
        return usesCode && this.form.coupon_ids.length > 0 && !this.selectedCodes.length
      },
      filteredCoupons () {
        const q = (this.couponSearch || '').trim().toLowerCase()
        if (!q) {
          return this.coupons
        }
        return this.coupons.filter(coupon => (coupon.name || '').toLowerCase().indexOf(q) !== -1)
      }
    },
    created () {
      const companyData = this.$session.get('selectedCompanyData') || {}
      this.currency = this.getCurrencySymbol(companyData.currency_iso)
      this.companyName = companyData.name || ''
      this.getCoupons()
      if (this.isEdit) {
        this.getCampaign()
      }
    },
    methods: {
      requestData () {
        return {
          token: this.$session.get('token'),
          company_id: this.$session.get('selectedCompany')
        }
      },
      defaultSettings (type) {
        if (type === AUTOMATION_TYPE_BIRTHDAY) {
          return {
            announce_enabled: true,
            announce_days_before: 3,
            announce_title: '',
            announce_message: '',
            day_title: '',
            day_message: '',
            send_time: DEFAULT_SEND_TIME
          }
        }
        if (type === AUTOMATION_TYPE_CART) {
          return {
            first_delay_minutes: 60,
            first_title: '',
            first_message: '',
            nocoupon_title: '',
            nocoupon_message: '',
            second_enabled: true,
            second_delay_minutes: 1440,
            second_title: '',
            second_message: '',
            quiet_enabled: true,
            quiet_from: '22:00',
            quiet_to: '09:00',
            coupon_cooldown_days: 7
          }
        }
        return {}
      },
      defaultFocus (type) {
        return type === AUTOMATION_TYPE_CART ? 'first' : 'day'
      },
      // 90 => "= 1h 30min" (helper next to the minutes inputs)
      durationText (minutes, withEquals = true) {
        const total = parseInt(minutes, 10)
        if (!total || total < 0) {
          return ''
        }
        const days = Math.floor(total / 1440)
        const hours = Math.floor((total % 1440) / 60)
        const mins = total % 60
        const parts = []
        if (days) {
          parts.push(days + this.$t('marketingAutomation.unitDay'))
        }
        if (hours) {
          parts.push(hours + 'h')
        }
        if (mins) {
          parts.push(mins + 'min')
        }
        return (withEquals ? '= ' : '') + parts.join(' ')
      },
      canPickType (item) {
        return item.available && !this.isEdit
      },
      pickType (item) {
        if (!this.canPickType(item) || this.form.type === item.value) {
          return
        }
        this.form.type = item.value
        this.form.settings = this.defaultSettings(item.value)
        this.focused = this.defaultFocus(item.value)
      },
      getCampaign () {
        this.loading = true
        this.apiRequest('/automation-campaign/' + this.campaignId, 'GET', this.requestData(), (response) => {
          this.loading = false
          if (!(response.status === true && response.data)) {
            return
          }
          const data = response.data
          this.form = {
            name: data.name || '',
            period: data.date_from && data.date_to ? [data.date_from, data.date_to] : null,
            active: !!data.active,
            type: data.type,
            settings: { ...this.defaultSettings(data.type), ...(data.settings || {}) },
            coupon_ids: Array.isArray(data.coupon_ids) ? data.coupon_ids.slice() : []
          }
          if (data.type === AUTOMATION_TYPE_BIRTHDAY && !this.form.settings.announce_days_before) {
            this.form.settings.announce_days_before = 3
          }
          this.focused = this.defaultFocus(data.type)
        })
      },
      getCoupons (callback) {
        this.apiRequest('/automation-campaign/coupons', 'GET', this.requestData(), (response) => {
          if (response.status === true) {
            this.coupons = response.data || []
          }
          if (callback) {
            callback()
          }
        })
      },
      /* ---------------- Preview / placeholders ---------------- */
      previewText (text) {
        if (!text) {
          return ''
        }
        const days = (this.form.settings && this.form.settings.announce_days_before) || 3
        let code = this.selectedCodes.length
          ? this.selectedCodes.join(', ')
          : (this.form.type === AUTOMATION_TYPE_CART ? 'COS10' : 'ZINASTERE20')
        // no coupon (or the push without coupon): {cod} goes out empty, cleaned like the API does
        if (!this.form.coupon_ids.length || this.activePush === 'nocoupon') {
          code = ''
        }
        let out = String(text).replace(/\{nume\}/g, 'Maria').replace(/\{zile\}/g, days).replace(/\{cod\}/g, code)
        if (!code) {
          out = out.replace(/[ \t]+([,.!?])/g, '$1').replace(/,(\s*[!?.])/g, '$1').replace(/:(\s*[!?.]|\s*$)/g, '$1').replace(/[ \t]{2,}/g, ' ').trim()
        }
        return out
      },
      // example texts keep the placeholders literally ({nume} is also vue-i18n syntax)
      // without coupons the examples don't promise a discount (variants without {cod})
      example (key) {
        const finalKey = (!this.form.coupon_ids.length && NO_COUPON_EXAMPLES[key]) || key
        return this.$t('marketingAutomation.' + finalKey, { nume: '{nume}', zile: '{zile}', cod: '{cod}' })
      },
      useExample (which) {
        const s = this.form.settings
        const push = PUSHES[which]
        const fields = { [push.title]: push.exampleTitle, [push.message]: push.exampleMessage }
        const apply = () => {
          Object.keys(fields).forEach(field => {
            s[field] = this.example(fields[field])
          })
          this.focused = which
        }
        const hasText = Object.keys(fields).some(field => (s[field] || '').trim())
        if (!hasText) {
          apply()
          return
        }
        swal({
          text: this.$t('marketingAutomation.useExampleConfirm'),
          type: 'question',
          showCancelButton: true
        }).then((result) => {
          if (result) {
            apply()
          }
        }).catch(() => {})
      },
      insertPlaceholder (key) {
        const field = PUSHES[this.activePush].message
        const current = this.form.settings[field] || ''
        if ((current + key).length > MESSAGE_MAX) {
          return
        }
        this.form.settings[field] = current + (current && !/\s$/.test(current) ? ' ' : '') + key
      },
      /* ---------------- Coupons ---------------- */
      isCouponChecked (id) {
        return this.form.coupon_ids.indexOf(id) !== -1
      },
      toggleCoupon (id) {
        const i = this.form.coupon_ids.indexOf(id)
        if (i === -1) {
          this.form.coupon_ids.push(id)
        } else {
          this.form.coupon_ids.splice(i, 1)
        }
      },
      couponDiscountText (coupon) {
        if (coupon.type === 'FREE_PRODUCT') {
          return this.$t('marketingCoupons.freeProduct')
        }
        return (coupon.discount || 0) + ' ' + (coupon.type === 'FIXED' ? this.currency : '%')
      },
      formatDay (value) {
        if (!value) {
          return '-'
        }
        const parts = String(value).substr(0, 10).split('-')
        return parts.length === 3 ? parts[2] + '.' + parts[1] + '.' + parts[0] : value
      },
      couponValidityText (coupon) {
        return this.formatDay(coupon.datetime_validfrom) + ' - ' + this.formatDay(coupon.datetime_validto)
      },
      couponExpired (coupon) {
        if (!coupon.datetime_validto) {
          return true
        }
        return String(coupon.datetime_validto).substr(0, 10) < new Date().toISOString().substr(0, 10)
      },
      openCouponModal () {
        this.couponModalOpen = true
        this.$modal.show('automationCouponModal')
      },
      closeCouponModal () {
        this.$modal.hide('automationCouponModal')
        this.couponModalOpen = false
      },
      onCouponModalOpened () {
        // `.main-panel` / the route modal have transforms: re-parent to <body> so it stays on top
        const el = document.querySelector('.automationCouponModal.vm--container')
        if (el && el.parentNode !== document.body) {
          document.body.appendChild(el)
        }
      },
      onCouponCreated (coupon) {
        this.closeCouponModal()
        this.getCoupons(() => {
          if (coupon && coupon.id && this.form.coupon_ids.indexOf(coupon.id) === -1) {
            this.form.coupon_ids.push(coupon.id)
          }
        })
      },
      /* ---------------- Save ---------------- */
      validate () {
        this.formErrors = {}
        const required = this.$t('validations.requiredField')
        const s = this.form.settings || {}
        if (!(this.form.name || '').trim()) {
          this.$set(this.formErrors, 'name', required)
        }
        if (!this.form.period || !this.form.period[0] || !this.form.period[1]) {
          this.$set(this.formErrors, 'period', required)
        }
        if (!this.form.type) {
          this.$set(this.formErrors, 'type', required)
        }
        if (this.form.type === AUTOMATION_TYPE_BIRTHDAY) {
          if (s.announce_enabled) {
            const days = Number(s.announce_days_before)
            if (!Number.isInteger(days) || days < 1 || days > ANNOUNCE_DAYS_MAX) {
              this.$set(this.formErrors, 'announce_days_before', this.$t('marketingAutomation.announceDaysInvalid', { max: ANNOUNCE_DAYS_MAX }))
            }
            if (!(s.announce_title || '').trim()) {
              this.$set(this.formErrors, 'announce_title', required)
            }
            if (!(s.announce_message || '').trim()) {
              this.$set(this.formErrors, 'announce_message', required)
            }
          }
          if (!(s.day_title || '').trim()) {
            this.$set(this.formErrors, 'day_title', required)
          }
          if (!(s.day_message || '').trim()) {
            this.$set(this.formErrors, 'day_message', required)
          }
        }
        if (this.form.type === AUTOMATION_TYPE_CART) {
          const between = (value, min, max) => Number.isInteger(Number(value)) && Number(value) >= min && Number(value) <= max && value !== ''
          const text = (key) => {
            if (!(s[key] || '').trim()) {
              this.$set(this.formErrors, key, required)
            }
          }
          if (!between(s.first_delay_minutes, DELAY_MIN, FIRST_DELAY_MAX)) {
            this.$set(this.formErrors, 'first_delay_minutes', this.$t('marketingAutomation.delayInvalid', { min: DELAY_MIN, max: FIRST_DELAY_MAX }))
          }
          text('first_title')
          text('first_message')
          if (this.form.coupon_ids.length) {
            text('nocoupon_title')
            text('nocoupon_message')
            if (!between(s.coupon_cooldown_days, 0, COOLDOWN_MAX)) {
              this.$set(this.formErrors, 'coupon_cooldown_days', this.$t('marketingAutomation.cooldownInvalid', { max: COOLDOWN_MAX }))
            }
          }
          if (s.second_enabled) {
            if (!between(s.second_delay_minutes, DELAY_MIN, SECOND_DELAY_MAX)) {
              this.$set(this.formErrors, 'second_delay_minutes', this.$t('marketingAutomation.delayInvalid', { min: DELAY_MIN, max: SECOND_DELAY_MAX }))
            }
            text('second_title')
            text('second_message')
          }
          if (s.quiet_enabled && s.quiet_from === s.quiet_to) {
            this.$set(this.formErrors, 'quiet_to', this.$t('marketingAutomation.quietInvalid'))
          }
        }
        return !Object.keys(this.formErrors).length
      },
      save () {
        if (!this.validate()) {
          return
        }

        const data = {
          ...this.requestData(),
          name: this.form.name,
          active: this.form.active ? 1 : 0,
          date_from: this.form.period[0],
          date_to: this.form.period[1],
          type: this.form.type,
          settings: this.form.settings,
          coupon_ids: this.form.coupon_ids
        }
        const url = this.isEdit ? '/automation-campaign/' + this.campaignId : '/automation-campaign'
        const method = this.isEdit ? 'PUT' : 'POST'

        this.saving = true
        this.apiRequest(url, method, data, (response) => {
          this.saving = false
          if (response.status === true) {
            swal({
              title: '',
              text: this.$t('marketingAutomation.saved'),
              type: 'success',
              showConfirmButton: false,
              timer: 1500
            }).catch(() => {})
            this.goBack()
            return
          }

          // API errors next to their fields (settings.day_title => day_title)
          const errors = response.errors || {}
          Object.keys(errors).forEach(key => {
            const message = Array.isArray(errors[key]) ? errors[key][0] : errors[key]
            const field = key.replace('settings.', '').replace(/^date_(from|to)$/, 'period')
            this.$set(this.formErrors, field, message)
          })
          const messages = Object.keys(errors).map(key => Array.isArray(errors[key]) ? errors[key][0] : errors[key])
          swal('', messages.join(' ') || this.$t('validations.wentWrong'), 'error').catch(() => {})
        })
      },
      goBack () {
        this.$bus.$emit('refresh')
        this.$bus.$emit('close-modal')
      }
    }
  }
</script>
<style>
  .automation-types {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  .automation-type-card {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 190px;
    padding: 10px 14px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    cursor: pointer;
    background: #fff;
  }
  .automation-type-card .fa {
    color: #7e57c2;
    font-size: 16px;
  }
  .automation-type-card.is-selected {
    border-color: #7e57c2;
    background: #f3eefb;
    box-shadow: 0 0 0 1px #7e57c2 inset;
  }
  .automation-type-card.is-disabled {
    cursor: not-allowed;
  }
  .automation-type-card.is-disabled:not(.is-selected) {
    opacity: 0.55;
  }
  .automation-type-card__soon {
    margin-left: auto;
    padding: 1px 6px;
    border-radius: 8px;
    font-size: 10px;
    background: #f5f5f5;
    color: #888;
    border: 1px solid #e0e0e0;
  }
  .automation-info {
    margin-bottom: 14px;
    padding: 8px 12px;
    border-radius: 6px;
    background: #f5faff;
    border: 1px solid #bbdefb;
    color: #1565c0;
    font-size: 13px;
  }
  .automation-box {
    margin-bottom: 14px;
    padding: 12px 14px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
  }
  .automation-box.is-off {
    background: #fafafa;
  }
  .automation-box__head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
  }
  .automation-box__head .fa {
    color: #7e57c2;
  }
  .automation-days__row {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .automation-duration {
    margin-left: 6px;
    color: #7e57c2;
    font-weight: 600;
  }
  .automation-box__hint {
    display: block;
    margin: -4px 0 10px;
  }
  .automation-variant {
    padding: 1px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
    background: #f5f5f5;
    color: #777;
    border: 1px solid #e0e0e0;
  }
  .automation-variant.is-coupon {
    background: #eef7ee;
    color: #2e7d32;
    border-color: #c8e6c9;
  }
  .automation-cooldown {
    margin-top: 6px;
  }
  .automation-cooldown__hint {
    display: block;
    margin-top: 6px;
  }
  .automation-quiet {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
  }
  .automation-quiet__time {
    width: 120px !important;
  }
  .automation-days__row input {
    max-width: 90px;
  }
  .automation-count {
    float: right;
    color: #aaa;
  }
  .automation-preview {
    padding: 12px 14px;
    border-radius: 12px;
    background: #f4f4f6;
    border: 1px solid #e6e6ea;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  }
  .automation-preview__title.is-example,
  .automation-preview__body.is-example {
    color: #9e9eb0;
    font-style: italic;
  }
  .automation-preview__note,
  .automation-preview__warn {
    display: block;
    margin-top: 6px;
  }
  .automation-preview__warn {
    color: #8d6e00;
  }
  .automation-example-link {
    margin-left: auto;
    font-size: 12px;
    cursor: pointer;
    color: #7e57c2;
  }
  .automation-example-link:hover {
    text-decoration: underline;
  }
  .automation-preview__app {
    font-size: 11px;
    color: #888;
    margin-bottom: 4px;
  }
  .automation-preview__title {
    font-weight: 600;
    color: #222;
    word-break: break-word;
  }
  .automation-preview__body {
    font-size: 13px;
    color: #444;
    white-space: pre-line;
    word-break: break-word;
  }
  .automation-placeholders {
    margin-top: 12px;
  }
  .automation-placeholder {
    margin-top: 4px;
  }
  .automation-placeholder code {
    cursor: pointer;
    margin-right: 6px;
  }
  .automation-placeholder.is-disabled code {
    cursor: not-allowed;
    opacity: 0.45;
  }
  .automation-placeholder.is-disabled > small:first-of-type {
    color: #aaa;
  }
  .automation-placeholder__off {
    display: block;
    margin: 2px 0 0 2px;
    font-size: 11px;
    color: #8d6e00;
  }
  .automation-coupons-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 4px;
  }
  .automation-selected-count {
    display: inline-block;
    margin-left: 8px;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 12px;
    text-transform: none;
    background: #eef7ee;
    color: #2e7d32;
    border: 1px solid #c8e6c9;
  }
  .automation-coupon-search {
    margin-bottom: 8px;
    max-width: 560px;
  }
  .automation-coupon-list {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
  }
  .automation-coupon-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    cursor: pointer;
    border-bottom: 1px solid #f0f0f0;
  }
  .automation-coupon-row.is-checked {
    background: #eef7ee;
  }
  .automation-coupon-code {
    margin-left: 4px;
    font-size: 11px;
  }
  .automation-coupon-flag {
    display: inline-block;
    margin-left: 6px;
    padding: 1px 6px;
    border-radius: 8px;
    font-size: 11px;
    background: #f5f5f5;
    color: #999;
    border: 1px solid #e0e0e0;
  }
  .automation-coupon-flag--warn {
    background: #fff8e1;
    color: #8d6e00;
    border-color: #ffe082;
  }
  .automationCouponModal.vm--container {
    z-index: 100500;
  }
  .automationCouponModal .vm--modal {
    top: 20px !important;
    left: 0 !important;
    right: 0 !important;
    margin: 0 auto !important;
    max-height: calc(100vh - 40px);
    overflow-y: auto;
  }
  .automation-coupon-modal__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 18px;
    border-bottom: 1px solid #ddd;
  }
  .automation-coupon-modal__header h4 {
    margin: 0;
  }
  .automation-coupon-modal__close {
    cursor: pointer;
    font-size: 18px;
    color: #777;
  }
  .automation-coupon-modal__body {
    padding: 10px 0;
  }
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Marketing/AutomationCampaigns/AutomationCampaignForm.vue